import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import { Alert, Box, Button, Divider, Stack, Typography, useTheme } from '@mui/material';
import useTenantName from 'hooks/useTenantName/useTenantName';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import { useWizardStepContext } from 'v2/components/WizardModal';
import useTenantRegistrationInfo from 'v2/hooks/onboarding/useTenantRegistrationInfo/useTenantRegistrationInfo';

const STEP_INDEX = 2;

const SyncingUsersStep: React.FC<React.PropsWithChildren> = () => {
  const [searchParams] = useSearchParams();
  const azureTenantId = searchParams.get('tenant');
  const { showSnackbar } = useSnackbarContext();
  const handleFetchError = (errorMessage: string) => showSnackbar(errorMessage, { severity: 'error' });
  const { tenantName, loading: loadingTenantName } = useTenantName({
    azureTenantId,
    handleFetchError
  });
  const { registerAzureTenant, registerAzureTenantLoading } = useTenantRegistrationInfo({
    handleFetchError
  });
  const { setFooterContent, steps, goToNext } = useWizardStepContext();
  const theme = useTheme();

  const handleStartButtonOnClick = async () => {
    if (!registerAzureTenantLoading && azureTenantId) {
      try {
        const result = await registerAzureTenant({ azureTenantId });
        if (result.data) goToNext();
      } catch (err: unknown) {
        handleFetchError(`Error while registering Azure Tenant`);
      }
    }
  };

  React.useLayoutEffect(() => {
    setFooterContent(
      STEP_INDEX,
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, gap: 2 }}>
        <Button
          key="device-group-name-step-submit"
          id="syncing-users-start-button"
          variant="contained"
          type="submit"
          loading={registerAzureTenantLoading}
          onClick={() => handleStartButtonOnClick()}
        >
          Start
        </Button>
      </Box>
    );
  }, [setFooterContent, steps]);

  return (
    <Stack flex="1 1 auto" flexDirection="column" gap={1}>
      {!loadingTenantName && <Typography variant="h4">Synchronizing users from {tenantName}</Typography>}
      <Stack flex="1 1 auto" flexDirection="column" gap={1} style={{ overflowY: 'auto' }}>
        <Divider />
        <Stack flexDirection="column" gap={1.5}>
          <Typography variant="body1" color="textPrimary">
            Synchronization of EntraID users can be done in two different ways:
          </Typography>
          <Stack margin="0 2rem">
            <Typography variant="body1" color="textPrimary">
              <b>"ATLAS" prefix</b> (Available to all tiers)
              <br /> Prefix the names of the user groups you wish to sync with "ATLAS" (e.g. "ATLAS Employees"). All
              groups with this prefix will be automatically synchronized with ATLAS.
              <br />
              <br />
              <b>ATLAS Sync app</b> (Enterprise tier only)
              <br /> Using the ATLAS Sync Enterprise app to select specific user groups in EntraID that should be synced
              with ATLAS
            </Typography>
          </Stack>
          <Stack flexDirection="column" gap={1.5}>
            <Alert severity="warning" icon={<WarningAmberOutlined sx={{ fill: theme.palette.warning.dark }} />}>
              <Typography variant="body1" style={{ color: theme.palette.warning.dark }}>
                <b>Important:</b> <br />
                ATLAS is role based access, and can only be synced with user groups. Not individual users.
              </Typography>
            </Alert>
          </Stack>
          <Typography variant="body1" color="textPrimary">
            To start the synchronization process and link the license to the organization, click on the start-button
            below.
          </Typography>
        </Stack>
        <Divider />
      </Stack>
    </Stack>
  );
};

export default SyncingUsersStep;
